import { Auth, Amplify } from 'aws-amplify'
import objectUtils from '../utils/object-utils'
import { toModel } from '../services/normalizer'
import { routeLeafApi } from './routeLeafApi'

const logNamespace = 'ROUTELEAF-AUTHENTICATOR'

export default class RouteLeafAuthenticator {
  constructor (options) {
    // Parse options
    this.debug = options.debug && options.debug === true
    this.region = options.region
    this.userPoolId = options.userPoolId
    this.webClientId = options.webClientId
    this.oauthSettings = options.oauth || {}

    // Instantiate member props
    this.token = null
    this.tokenExpiresAt = -1

    // Initialize amplify config
    this.init()
  }

  // ---------------------------------------------------------------------------
  // creds = {
  //   email: <email>,
  //   password: <password>
  // }
  // ---------------------------------------------------------------------------
  async authenticateUser (creds = {}) {
    if (!creds.username || !creds.password) {
      handleErrorResponse(400, 'Please provide an email and password.')
    }

    if (this.debug) console.log(`[${logNamespace}] authenticateUser - logging in user...`)

    try {
      // Authenticate with Cognito
      const authInfo = await Auth.signIn({
        username: creds.username,
        password: creds.password
      })
      if (this.debug) console.log(`[${logNamespace}] authenticateUser - reply from authenticator:`, authInfo)

      // Extract token info
      this.token = objectUtils.get(authInfo, 'signInUserSession.idToken.jwtToken', null)
      this.tokenExpiresAt = objectUtils.get(authInfo, 'signInUserSession.idToken.payload.exp', -1)

      return {
        provider: 'cognito',
        token: this.token,
        expires_at: this.tokenExpiresAt
      }
    } catch (error) {
      if (this.debug) console.log(`[${logNamespace}] authenticateUser =>`, error)
      throw error
    }
  }

  async fetchUserInfo (options = {}) {
    const provider = options.provider
    const token = options.token
    if (this.debug) console.log(`[${logNamespace}] fetchUserInfo - fetching ${provider} user info...`)

    try {
      // Access active user
      const uri = '/my/user'
      const headers = { authorization: `Bearer ${token}` }
      const payload = await routeLeafApi.get(uri, { headers })

      if (!payload) handleErrorResponse(404, 'No active user.')
      if (this.debug) console.log(`[${logNamespace}] fetchUserInfo - ${provider} payload:`, payload)

      // Build user info payload
      const user = toModel(payload)
      if (this.debug) console.log(`[${logNamespace}] fetchUserInfo - hydrated user:`, user)

      return user
    } catch (error) {
      if (this.debug) console.log(`[${logNamespace}] fetchUserInfo =>`, error)
      throw error
    }
  }

  async validateSession () {
    if (this.debug) console.log(`[${logNamespace}] validateSession - validating session...`)

    try {
      // Obtain active session
      const authInfo = await Auth.currentSession()

      // Extract token info
      this.token = objectUtils.get(authInfo, 'idToken.jwtToken', null)
      this.tokenExpiresAt = objectUtils.get(authInfo, 'idToken.payload.exp', -1)

      return {
        provider: 'cognito',
        token: this.token,
        expires_at: this.tokenExpiresAt
      }
    } catch (error) {
      if (this.debug) console.log(`[${logNamespace}] validateSession =>`, error)
      throw error
    }
  }

  async expireUser (provider) {
    if (this.debug) console.log(`[${logNamespace}] expireUser (${provider}) - logging out user...`)

    try {
      // Logout from Cognito
      await Auth.signOut()

      const reply = {
        success: true,
        message: 'You have been logged-out.'
      }

      return reply
    } catch (error) {
      if (this.debug) console.log(`[${logNamespace}] expireUser =>`, error)
      throw error
    }
  }

  init () {
    Amplify.configure({
      Auth: {
        // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
        // identityPoolId: 'XX-XXXX-X:XXXXXXXX-XXXX-1234-abcd-1234567890ab',

        // Cognito region
        region: this.region,

        // Cognito user pool ID
        userPoolId: this.userPoolId,

        // Cognito web client ID
        userPoolWebClientId: this.webClientId,

        // Enforce user authentication prior to accessing AWS resources or not
        mandatorySignIn: false,

        // The authentication flow type
        authenticationFlowType: 'USER_PASSWORD_AUTH',

        oauth: {
          ...this.oauthSettings
        }
      }
    })
  }
}

// ----------------------------------------------------------- Response handlers

function handleErrorResponse (status = 500, message = '') {
  const error = new Error(message)
  error.status = status
  error.mesage = message
  throw error
}
